import {
  BLACK,
  type BaseExcelColor,
  ExcelColor,
  TRANSPARENT,
} from './excelColor';
import { type BaseStyle, type StyleToMerge, mergeStyles } from './types';
import {
  type AvailableFont,
  googleFontFamilies,
  standardFonts,
} from '@/fonts/fonts';
import { nanoid } from 'nanoid';
import { stringifyCSSProperties } from 'react-style-stringify';

function mergeRichTextStyles(
  base: BaseStyle<RichTextStyle>,
  other: StyleToMerge<RichTextStyle>,
) {
  base = { ...base };
  delete base.background;

  return mergeStyles(base, other);
}

export type RichTextStyle = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  color: BaseExcelColor;
  size: number;
  font: string;
  background: BaseExcelColor;
};

export const DEFAULT_RICH_TEXT_STYLE: RichTextStyle = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  color: BLACK,
  size: 12,
  font: 'Helvetica',
  background: TRANSPARENT,
};

export class RichTextRun {
  constructor(
    public text: string = '',
    public style: BaseStyle<RichTextStyle> = {},
    public readonly id = nanoid(),
  ) {}

  public withText(text: string): RichTextRun {
    this.text = text;
    return this;
  }

  public withStyle(style: StyleToMerge<RichTextStyle>): RichTextRun {
    this.style = mergeStyles(this.style, style);
    return this;
  }

  /**
   * Try to merge this run with the given run. Always returns new instances.
   */
  public tryMerge(
    run: RichTextRun,
  ): [RichTextRun] | [RichTextRun, RichTextRun] {
    if (run.text === '') {
      return [this.clone()];
    }
    if (styleEquals(this.style, run.style)) {
      return [this.withText(this.text + run.text)];
    }
    return [this.clone(), run.clone()];
  }

  public split(at: number): [RichTextRun | null, RichTextRun | null] {
    if (at === 0) {
      return [null, this];
    }
    if (at === this.text.length) {
      return [this, null];
    }
    return [
      new RichTextRun(this.text.slice(0, at), this.style),
      new RichTextRun(this.text.slice(at), this.style),
    ];
  }

  public toHtml(baseStyle: BaseStyle<RichTextStyle>): HTMLSpanElement {
    const style = stringifyCSSProperties(
      richTextStyleToCss(mergeRichTextStyles(baseStyle, this.style)),
    );
    const span = document.createElement('span');
    span.id = this.id;
    span.style.cssText = style;
    span.appendChild(document.createTextNode(this.text));
    return span;
  }

  public updateHtml(element: HTMLElement, baseStyle: BaseStyle<RichTextStyle>) {
    if (element.id !== this.id) {
      throw new Error('id mismatch');
    }
    const style = stringifyCSSProperties(
      richTextStyleToCss(mergeRichTextStyles(baseStyle, this.style)),
    );
    element.style.cssText = style;
    if (element.textContent !== this.text) {
      // only update text if necessary
      element.textContent = this.text;
    }
  }

  public updateFromHtml(element: HTMLElement) {
    if (element.id !== this.id) {
      throw new Error('id mismatch');
    }
    // style should never be changed like this
    this.text = element.textContent;
  }

  public toPlainText(): string {
    return this.text;
  }

  public clone(): RichTextRun {
    return new RichTextRun(this.text, this.style, this.id);
  }
}

export class RichText {
  private runsById: Map<string, RichTextRun> = new Map();
  private runs: RichTextRun[] = [];

  public static of(text: string | RichText): RichText {
    if (typeof text === 'string') {
      return new RichText().withRun(new RichTextRun(text));
    }
    return text;
  }

  public flatten(): RichText {
    return new RichText().withRuns(flattenRuns(this.runs));
  }

  public withRun(run: RichTextRun): RichText {
    this.runs.push(run);
    this.runsById.set(run.id, run);
    return this;
  }

  public withRuns(runs: RichTextRun[]): RichText {
    this.runs = runs;
    this.runsById = new Map(runs.map(run => [run.id, run]));
    return this;
  }

  public byId(id: string): RichTextRun | undefined {
    return this.runsById.get(id);
  }

  public getIndex(run: RichTextRun): number {
    return this.runs.indexOf(run);
  }

  public getRuns(): readonly RichTextRun[] {
    return this.runs;
  }

  /**
   * Replaces a range of runs with new runs.
   *
   * Assumes the original runs are contiguous.
   */
  public replaceWith(ogs: RichTextRun[], news: RichTextRun[]) {
    const start = this.runs.indexOf(ogs[0]!);
    const end = this.runs.indexOf(ogs[ogs.length - 1]!) + 1;
    this.runs.splice(start, end - start, ...news);

    for (const run of ogs) {
      this.runsById.delete(run.id);
    }
    for (const run of news) {
      this.runsById.set(run.id, run);
    }
  }

  public toHtml(baseStyle: BaseStyle<RichTextStyle>): DocumentFragment {
    const fragment = document.createDocumentFragment();
    for (const run of this.runs) {
      fragment.appendChild(run.toHtml(baseStyle));
    }
    return fragment;
  }

  public updateHtml(element: HTMLElement, baseStyle: BaseStyle<RichTextStyle>) {
    const children = element.childNodes;

    for (let i = 0; i < children.length; i++) {
      const child = children[i]!;
      if (child.nodeType !== Node.ELEMENT_NODE) {
        child.remove();
        // there should only be elements
        continue;
      }

      const el = child as HTMLElement;

      const expectedRun = this.runs[i];
      if (!expectedRun) {
        el.remove();
        // ran out of runs. any remaining children should be removed
        continue;
      }

      const id = el.id;

      if (id !== expectedRun.id) {
        const newEl = expectedRun.toHtml(baseStyle);
        el.before(newEl);
        // id mismatch. insert new element before. maybe the next run will match
        continue;
      }

      expectedRun.updateHtml(el, baseStyle);
    }
  }

  public updateFromHtml(element: HTMLElement) {
    const children = element.childNodes;

    outer: for (let i = 0; i < children.length; i++) {
      const child = children[i]!;
      if (child.nodeType !== Node.ELEMENT_NODE) {
        console.error('Expected element node. Got', child);
        continue;
      }

      const el = child as HTMLElement;

      let expectedRun = this.runs[i];

      do {
        expectedRun = this.runs[i];
        if (!expectedRun) {
          console.error('ran out of runs. ', this.runs);
          continue outer;
        }
        if (expectedRun.id === el.id) break;

        this.runs.splice(i, 1);
      } while (1);

      expectedRun.updateFromHtml(el);
    }
  }

  public toPlainText(): string {
    return this.runs.map(run => run.toPlainText()).join('');
  }
}

export function richTextToHtml(
  richText: RichText | string,
  baseStyle: BaseStyle<RichTextStyle>,
): DocumentFragment | HTMLSpanElement {
  if (typeof richText === 'string') {
    return new RichTextRun(richText, {}).toHtml(baseStyle);
  }
  return richText.toHtml(baseStyle);
}

export function richTextStyleToCss(
  style: BaseStyle<RichTextStyle>,
): React.CSSProperties {
  const ret: React.CSSProperties = {};

  if (style.bold) {
    ret.fontWeight = '700';
  }
  if (style.italic) {
    ret.fontStyle = 'italic';
  }
  ret.textDecoration = '';
  if (style.underline) {
    ret.textDecoration += 'underline ';
  }
  if (style.strikethrough) {
    ret.textDecoration += 'line-through ';
  }
  if (style.color) {
    ret.color = style.color.toHex();
  }
  if (style.size) {
    ret.fontSize = `${style.size}px`;
  }
  if (style.font) {
    ret.fontFamily = style.font;
  }
  if (style.background) {
    ret.backgroundColor = style.background.toHex();
  }

  return ret;
}

export const DEFAULT_CSS_PROPERTIES: React.CSSProperties = richTextStyleToCss(
  DEFAULT_RICH_TEXT_STYLE,
);

export function styleEquals(
  a: BaseStyle<RichTextStyle>,
  b: BaseStyle<RichTextStyle>,
): boolean {
  return (
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.underline === b.underline &&
    a.strikethrough === b.strikethrough &&
    a.color === b.color &&
    a.size === b.size &&
    a.font === b.font &&
    a.background === b.background
  );
}

/**
 * Clones and flattens runs.
 */
export function flattenRuns(runs: RichTextRun[]): RichTextRun[] {
  const ret: RichTextRun[] = [];

  for (const run of runs) {
    const last = ret[ret.length - 1];
    if (!last) {
      ret.push(run.clone());
      continue;
    }
    const merged = last.tryMerge(run);
    if (merged.length === 1) {
      ret[ret.length - 1] = merged[0];
      continue;
    }
    ret.push(merged[1]);
  }
  return ret;
}

export function plainText(richText: RichText | string): string {
  if (typeof richText === 'string') {
    return richText;
  }
  return richText.toPlainText();
}

export function richText(text: RichText | string): RichText {
  if (typeof text === 'string') {
    return new RichText().withRun(new RichTextRun(text));
  }
  return text;
}
